import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { PASSWORD_MIN_LENGTH, PASSWORD_REGEX } from 'src/app/helpers/regex.helper';
import { LocationRegistration } from 'src/app/models/location-registration.model';
import { EmployeeRegisterRequest } from 'src/app/models/requests/employee-register.model';
import { LocalStorageService } from 'src/app/services/local/local-storage.service';
import { LocationRegistrationService } from 'src/app/services/repositories/shared-api/location-registration.service';
import { EyeValidators } from 'src/app/validators/eyeValidators';

@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.scss'],
    standalone: false
})
export class SignUpComponent implements OnInit {
  private readonly N_REGISTRATION_ERROR_TITLE = 'Registration';
  private readonly N_REGISTRATION_LINK_ERROR_MSG = 'This registration link is invalid. Please verify that the link is correct.';
  private readonly TaskBlurIcon = 'assets/images/icons/task.svg'
  private readonly TaskGreenIcon = 'assets/images/icons/task-green.svg'

  locationRegistration: LocationRegistration | undefined;
  passwordMinLength = PASSWORD_MIN_LENGTH;
  showPassword: boolean = false
  showConfirmPassword: boolean = false
  passwordRules: {
    icon: string,
    content: string,
    partern: RegExp,
    isHighlight: boolean
  }[] = [
      {
        icon: this.TaskBlurIcon,
        partern: /^.{10,}$/,
        content: "At least 10 characters.",
        isHighlight: false
      },
      {
        icon: this.TaskBlurIcon,
        partern: /[a-z]/,
        content: "At least 1 lowercase character.",
        isHighlight: false
      },
      {
        icon: this.TaskBlurIcon,
        partern: /[A-Z]/,
        content: "At least 1 uppercase character.",
        isHighlight: false
      },
      {
        icon: this.TaskBlurIcon,
        partern: /^(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]).+$/,
        content: "At least 1 number or special (@, #, $,...) character.",
        isHighlight: false
      }
    ]

  get sfc() { return this.signupForm.controls }

  signupForm: UntypedFormGroup = new UntypedFormGroup({
    firstName: new UntypedFormControl('', [Validators.required]),
    lastName: new UntypedFormControl('', [Validators.required]),
    emailAddress: new UntypedFormControl('', [Validators.required, Validators.email]),
    newPassword: new UntypedFormControl('', [Validators.required,
    Validators.minLength(PASSWORD_MIN_LENGTH),
    Validators.pattern(PASSWORD_REGEX),
    ]),
    confirmPassword: new UntypedFormControl('', [Validators.required]),
  },
    {
      validators: EyeValidators.MustMatch('newPassword', 'confirmPassword')
    });

  constructor(private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _localStorageService: LocalStorageService,
    private _locationRegistrationService: LocationRegistrationService,
    private _notificationService: ToastrService) { }

  async ngOnInit(): Promise<void> {
    this._activatedRoute.queryParams
      .subscribe(params => {
        this.lookupAccount(params.locRef);
      });


    this.setDefaultValue()
    this.onChangesPassword()
  }

  lookupAccount(locRef: string) {
    if (locRef) {
      this._locationRegistrationService.getLocationInfo(locRef)
        .subscribe((data: LocationRegistration) => {
          this.locationRegistration = data
        }, (error) => {
          this.showRegistrationError();
        });
    }
    else {
      this.showRegistrationError();
    }
  }

  showRegistrationError() {
    this._notificationService.error(this.N_REGISTRATION_LINK_ERROR_MSG, this.N_REGISTRATION_ERROR_TITLE);
  }

  onTogglePasswordButtonClick() {
    this.showPassword = !this.showPassword
  }

  onToggleConfirmPasswordButtonClick() {
    this.showConfirmPassword = !this.showConfirmPassword
  }

  setDefaultValue() {
    const empReg = this._localStorageService.getRegEmployee();

    if (empReg)
      this.signupForm.patchValue({
        firstName: empReg?.firstName,
        lastName: empReg?.lastName,
        emailAddress: empReg?.emailAddress,
        newPassword: empReg?.password,
        confirmPassword: empReg?.password,
      });
  }

  onChangesPassword() {
    ['newPassword'].forEach(key => {
      this.signupForm.get(key)?.valueChanges.subscribe(value => {
        this.passwordRules = this.passwordRules.map(rule => {
          const isSuccess = rule.partern.test(value)

          return {
            ...rule,
            isHighlight: isSuccess,
            icon: isSuccess ? this.TaskGreenIcon : this.TaskBlurIcon
          };
        });
      });
    });
  }

  onSubmit() {
    if (this.signupForm.invalid) {
      this.signupForm.markAllAsTouched();

      return;
    }

    if (this.locationRegistration) {
      let empReg = new EmployeeRegisterRequest()
      empReg.firstName = this.sfc.firstName.value;
      empReg.lastName = this.sfc.lastName.value;
      empReg.companyId = this.locationRegistration?.companyId;
      empReg.locationId = this.locationRegistration?.locationId;
      empReg.departmentId = this.locationRegistration?.departmentId;
      empReg.departmentId = this.locationRegistration?.departmentId;
      empReg.emailAddress = this.sfc.emailAddress.value;
      empReg.username = this.sfc.emailAddress.value;
      empReg.password = this.sfc.newPassword.value;
      this._localStorageService.setEmployeeRegAccountInfo(empReg);
      this._router.navigate([`/sign-up/address-info`], { queryParamsHandling: 'merge', preserveFragment: true });
    } else {
      this._notificationService.error('No location registration data was found. We cannot proceed to the next step.', 'Location registration');
    }
  }
}

